import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ShoppingCart, Star, ArrowRight, Package, Truck, Clock, Shield } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { useCart } from '../hooks/useCart';
import { productService } from '../services/productService';
import { categoryService } from '../services/categoryService';
import type { FirebaseProduct } from '../types/product';
import ProductCard from '../components/common/ProductCard';
import PaperSearch from '../components/home/PaperSearch';
import { formatPrice } from '../utils/helpers';
import CategorySection from '../components/home/CategorySection';
import HomeBlueBanner from '../components/home/HomeBlueBanner';

const Home: React.FC = () => {
  const { addToCart } = useCart();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupedProducts, setGroupedProducts] = useState<{
    [category: string]: FirebaseProduct[];
  }>({});
  const [categories, setCategories] = useState<string[]>([]);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('popularity');

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        setLoading(true);
        // First fetch all categories
        const categoriesData = await categoryService.getAllCategories();
        const categoryNames = categoriesData.map(cat => cat.name);
        setCategories(categoryNames);
        
        // Then fetch featured products
        const products = await productService.getProducts({ featured: true });
        
        // Group products by category
        const grouped = products.reduce((acc, product) => {
          if (!acc[product.category]) {
            acc[product.category] = [];
          }
          acc[product.category].push(product);
          return acc;
        }, {} as { [key: string]: FirebaseProduct[] });
        
        setGroupedProducts(grouped);
      } catch (err) {
        console.error('Error fetching featured products:', err);
        setError('Failed to load featured products');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  // Get all featured products (flattened)
  const allFeaturedProducts = Object.values(groupedProducts).flat();

  // Filter products based on selected category
  const displayedProducts = selectedCategory === 'all'
    ? allFeaturedProducts
    : groupedProducts[selectedCategory] || [];

  // Sort products based on selected sort option
  const sortedProducts = [...displayedProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price_asc':
        return (a.price ?? 0) - (b.price ?? 0);
      case 'price_desc':
        return (b.price ?? 0) - (a.price ?? 0);
      case 'rating':
        return ((b.rating || 0) - (a.rating || 0));
      default: // popularity
        return (b.reviewCount || 0) - (a.reviewCount || 0);
    }
  });

  // Filter products based on search query
  const filteredProducts = searchQuery 
    ? sortedProducts.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.brand.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sortedProducts;

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-blue-500"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{error}</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Add other sections like hero, etc. here */}
      
      {/* Blue Banner - Added Here */}
      <HomeBlueBanner />

      {/* Featured Products Section - Catalog Style */}
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Products</h2>
          <p className="text-gray-600">Discover our selection of premium papers and office supplies</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters - Desktop */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
              <h3 className="font-semibold text-gray-900 mb-6">Categories</h3>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    className="form-radio text-brand-blue-500"
                    checked={selectedCategory === 'all'}
                    onChange={() => setSelectedCategory('all')}
                  />
                  <span className="ml-2 text-gray-700">All Categories</span>
                </label>
                {categories.map((category) => (
                  <label key={category} className="flex items-center">
                    <input
                      type="radio"
                      className="form-radio text-brand-blue-500"
                      checked={selectedCategory === category}
                      onChange={() => setSelectedCategory(category)}
                    />
                    <span className="ml-2 text-gray-700">{category}</span>
                  </label>
                ))}
              </div>

              {/* Rest of the filter sections... */}
              <div className="mt-8 border-t pt-6">
                <h3 className="font-semibold text-gray-900 mb-4">Paper Weight (GSM)</h3>
                <div className="space-y-2">
                  {[70, 80, 100, 120, 160, 200, 250].map((gsm) => (
                    <label key={gsm} className="flex items-center">
                      <input type="checkbox" className="form-checkbox text-brand-blue-500 rounded" />
                      <span className="ml-2 text-gray-700">{gsm} GSM</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="mt-8 border-t pt-6">
                <h3 className="font-semibold text-gray-900 mb-4">Paper Size</h3>
                <div className="space-y-2">
                  {['A4', 'A3', 'A5', 'Legal', 'Letter'].map((size) => (
                    <label key={size} className="flex items-center">
                      <input type="checkbox" className="form-checkbox text-brand-blue-500 rounded" />
                      <span className="ml-2 text-gray-700">{size}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="mt-8 border-t pt-6">
                <h3 className="font-semibold text-gray-900 mb-4">Brands</h3>
                <div className="space-y-2">
                  {['Double A', 'HP Papers', 'Xerox', 'Navigator', 'Mondi', 'IK Plus'].map((brand) => (
                    <label key={brand} className="flex items-center">
                      <input type="checkbox" className="form-checkbox text-brand-blue-500 rounded" />
                      <span className="ml-2 text-gray-700">{brand}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="mt-8">
                <Link 
                  to="/products" 
                  className="w-full bg-brand-blue-500 text-white py-2 px-4 rounded-lg inline-flex items-center justify-center hover:bg-brand-blue-600 transition-colors"
                >
                  View Full Catalog
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Mobile Filters */}
            <div className="lg:hidden bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="all">All Categories</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="popularity">Most Popular</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                    <option value="rating">Highest Rated</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Desktop Sort */}
            <div className="hidden lg:flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-medium">
                  {selectedCategory === 'all' ? 'All Featured Products' : `${selectedCategory} Products`}
                </h3>
                <p className="text-sm text-gray-600">{filteredProducts.length} products found</p>
              </div>
              
              <select
                className="px-4 py-2 bg-white rounded-lg border shadow-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="popularity">Most Popular</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
              </select>
            </div>

            {/* Products Grid */}
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <Star className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-600 mb-4">Try adjusting your search criteria</p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('all');
                  }}
                  className="text-brand-blue-500 hover:text-brand-blue-600 font-medium"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={addToCart}
                  />
                ))}
              </div>
            )}

            {/* View All Link */}
            <div className="mt-8 text-center">
              <Link 
                to="/products" 
                className="inline-flex items-center px-6 py-3 bg-brand-blue-500 hover:bg-brand-blue-600 text-white rounded-lg font-medium transition-colors"
              >
                View All Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Success Toast Notification */}
      {showSuccessToast && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in z-50">
          <Star className="h-5 w-5" />
          Item added to cart
        </div>
      )}
    </>
  );
};

export default Home;